import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, RotateCcw, MessageSquare, Phone, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  {
    key: 'sms',
    label: 'SMS message',
    Icon: Smartphone,
    sub: 'Sent via Twilio the moment a new lead arrives. Keep it under 160 characters to stay in 1 SMS.',
    placeholder: 'Hi {name}, thanks for your interest in True Global Education...',
    accent: 'from-sky-500/20 to-sky-500/0',
    ring: 'ring-sky-500/30',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp message',
    Icon: MessageSquare,
    sub: 'Sent via Twilio WhatsApp right after the SMS. Emojis welcome.',
    placeholder: 'Hi {name} 👋 This is True Global Education...',
    accent: 'from-emerald-500/20 to-emerald-500/0',
    ring: 'ring-emerald-500/30',
  },
  {
    key: 'voice',
    label: 'Voice call script',
    Icon: Phone,
    sub: 'AI voice (OpenAI Nova) reads this aloud during the auto-call. 2–3 sentences works best.',
    placeholder: 'Hello {name}, this is True Global Education calling about your study-abroad enquiry...',
    accent: 'from-violet-500/20 to-violet-500/0',
    ring: 'ring-violet-500/30',
  },
];

export default function MessageTemplates() {
  const [vals, setVals] = useState({ sms: '', whatsapp: '', voice: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState('Priya');

  useEffect(() => { fetchTemplates(); }, []);

  async function fetchTemplates() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/follow-up-templates`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load templates');
      const d = await r.json();
      setVals({ sms: d.sms || '', whatsapp: d.whatsapp || '', voice: d.voice || '' });
      setDirty(false);
    } catch (e) {
      toast.error(e.message || 'Could not load templates');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/admin/follow-up-templates`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vals),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      const d = await r.json();
      setVals({ sms: d.sms, whatsapp: d.whatsapp, voice: d.voice });
      setDirty(false);
      toast.success('Templates saved — they apply to every new lead from now on.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  function reset(key) {
    setVals((v) => ({ ...v, [key]: '' }));
    setDirty(true);
  }

  function update(key, value) {
    setVals((v) => ({ ...v, [key]: value }));
    setDirty(true);
  }

  function renderPreview(template) {
    if (!template) return '— preview will appear here —';
    return template.replaceAll('{name}', preview || 'there');
  }

  if (loading) {
    return <div className="text-tge-muted text-sm p-8">Loading templates…</div>;
  }

  return (
    <div className="space-y-6" data-testid="message-templates-page">
      <div className="bg-white rounded-2xl border border-tge-navy/5 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-serif-display text-xl text-tge-navy">How it works</h3>
            <p className="text-sm text-tge-muted mt-1 max-w-2xl leading-relaxed">
              The instant a new lead arrives (Facebook, Instagram, Google or manual), the platform sends them
              an automated <strong>SMS → WhatsApp → AI voice call</strong> using these exact templates.
              Use <code className="bg-tge-cream-soft px-1.5 py-0.5 rounded text-[12px] text-tge-navy">{`{name}`}</code> as a
              placeholder — it gets replaced with the lead's first name. Empty templates revert to the system default.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-tge-muted">Preview for</label>
            <input
              data-testid="preview-name-input"
              value={preview}
              onChange={(e) => setPreview(e.target.value)}
              className="bg-tge-cream-soft border-0 rounded-lg px-3 py-1.5 text-sm font-medium text-tge-navy w-32 focus:outline-none focus:ring-2 focus:ring-tge-gold/40"
              placeholder="Lead name"
            />
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {CHANNELS.map((c) => (
          <div
            key={c.key}
            className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden"
            data-testid={`channel-${c.key}`}
          >
            <div className={`bg-gradient-to-r ${c.accent} px-6 py-4 flex items-center gap-3`}>
              <div className={`w-10 h-10 rounded-xl bg-white shadow-sm ring-1 ${c.ring} flex items-center justify-center`}>
                <c.Icon size={18} className="text-tge-navy" strokeWidth={1.8} />
              </div>
              <div className="flex-1">
                <h4 className="font-serif-display text-lg text-tge-navy leading-tight">{c.label}</h4>
                <p className="text-xs text-tge-muted mt-0.5">{c.sub}</p>
              </div>
              <button
                onClick={() => reset(c.key)}
                data-testid={`reset-${c.key}`}
                className="inline-flex items-center gap-1.5 text-[11px] text-tge-muted hover:text-tge-navy px-2.5 py-1 rounded-full hover:bg-white/60 transition"
                title="Clear to restore the system default"
              >
                <RotateCcw size={11} /> Reset
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-5 p-6">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">Editable template</label>
                <Textarea
                  value={vals[c.key]}
                  onChange={(e) => update(c.key, e.target.value)}
                  placeholder={c.placeholder}
                  rows={c.key === 'sms' ? 4 : 6}
                  className="mt-2 font-mono text-sm leading-relaxed"
                  data-testid={`template-${c.key}-textarea`}
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[11px] text-tge-muted">
                    {c.key === 'sms' && `${vals[c.key].length}/160 chars`}
                    {c.key !== 'sms' && `${vals[c.key].length} chars`}
                  </span>
                  {!vals[c.key] && (
                    <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Will use system default</span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-tge-muted font-semibold">Live preview</label>
                <div
                  className="mt-2 bg-tge-cream-soft rounded-xl p-4 text-sm text-tge-navy leading-relaxed min-h-[120px] whitespace-pre-wrap"
                  data-testid={`preview-${c.key}`}
                >
                  {renderPreview(vals[c.key] || c.placeholder)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 z-10">
        <div className="bg-white/95 backdrop-blur-md border border-tge-navy/10 rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg">
          <div className="text-sm text-tge-muted">
            {dirty ? (
              <span className="text-amber-700 font-medium">You have unsaved changes</span>
            ) : (
              <span>All changes saved.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={!dirty || saving} onClick={fetchTemplates} data-testid="discard-btn">
              Discard
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={save}
              className="bg-tge-navy hover:bg-tge-navy-deep"
              data-testid="save-templates-btn"
            >
              <Save size={14} className="mr-1.5" />
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
